import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Contexte d'agent « Agir dans la plateforme ».
// ISOLÉ PAR COMPTE : purgé à tout changement d'utilisateur / déconnexion. Aucune donnée persistée.
// L'agent n'exécute que des étapes en liste blanche renvoyées par le serveur (/assistant/plan),
// le périmètre étant re-contrôlé côté serveur à chaque action et chaque téléchargement.
const AgentContext = createContext(null);
export const useAgent = () => useContext(AgentContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function AgentProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [plan, setPlan] = useState(null);          // {vehicle, period, steps, ...}
  const [status, setStatus] = useState("idle");    // idle|running|paused|awaiting|error|done
  const [stepStates, setStepStates] = useState([]); // 'pending'|'active'|'done'|'error'
  const [logs, setLogs] = useState([]);            // [{text, kind}]
  const [report, setReport] = useState(null);      // {pdf_url, csv_url, scope_label}
  const [highlight, setHighlight] = useState(null);// 'vehicle'|'period'|'load'|null
  const [histCmd, setHistCmd] = useState(null);    // commande courante lue par la page Historique

  const controlRef = useRef("idle");               // running|paused|stopped (source de vérité runner)
  const ackRef = useRef({});                        // nonce -> {resolve}
  const expectedPathRef = useRef(null);             // route attendue (pour détecter nav manuelle)
  const runIdRef = useRef(0);

  const log = useCallback((text, kind = "info") => setLogs((l) => [...l, { text, kind }]), []);

  // ---- primitives de coordination avec la page cible ----
  const ack = useCallback((nonce, payload) => {
    const a = ackRef.current[nonce];
    if (a) { delete ackRef.current[nonce]; a.resolve(payload); }
  }, []);
  const ready = useCallback((route) => {
    if (route === "historique") ack("__mount_historique__", true);
  }, [ack]);

  const waitAck = (nonce, timeoutMs) => new Promise((resolve, reject) => {
    ackRef.current[nonce] = { resolve };
    setTimeout(() => {
      if (ackRef.current[nonce]) { delete ackRef.current[nonce]; reject(new Error("timeout")); }
    }, timeoutMs);
  });

  const waitWhilePaused = async () => {
    while (controlRef.current === "paused") {
      await sleep(250);
      if (controlRef.current === "stopped") break;
    }
    if (controlRef.current === "stopped") throw new Error("__stopped__");
  };

  // ---- contrôles ----
  const pause = useCallback(() => { if (controlRef.current === "running") { controlRef.current = "paused"; setStatus("paused"); log("Scénario en pause.", "warn"); } }, [log]);
  const resume = useCallback(() => { if (controlRef.current === "paused") { controlRef.current = "running"; setStatus("running"); log("Reprise du scénario.", "info"); } }, [log]);
  const stop = useCallback(() => { if (["running", "paused", "awaiting"].includes(controlRef.current) || status === "running") { controlRef.current = "stopped"; setStatus("idle"); setHistCmd(null); setHighlight(null); log("Scénario arrêté. Étapes restantes annulées.", "warn"); } }, [log, status]);
  const clear = useCallback(() => {
    controlRef.current = "idle"; runIdRef.current += 1; ackRef.current = {};
    setPlan(null); setStatus("idle"); setStepStates([]); setLogs([]); setReport(null); setHighlight(null); setHistCmd(null);
  }, []);

  // ---- exécution d'un plan ----
  const runPlan = useCallback(async (p) => {
    const myRun = ++runIdRef.current;
    controlRef.current = "running";
    setPlan(p); setReport(null); setLogs([]);
    setStepStates(p.steps.map(() => "pending"));
    setStatus("running");
    const setStep = (i, st) => setStepStates((arr) => arr.map((x, idx) => (idx === i ? st : x)));
    const vid = p.vehicle_id || null;

    try {
      for (let i = 0; i < p.steps.length; i++) {
        if (runIdRef.current !== myRun) return;
        await waitWhilePaused();
        const s = p.steps[i];
        setStep(i, "active");
        log(s.comment, "step");

        if (s.action === "navigate") {
          expectedPathRef.current = s.params.route;
          if (window.location.pathname !== s.params.route) {
            navigate(s.params.route);
            await waitAck("__mount_historique__", 12000);
          }
          await sleep(500);
        } else if (s.action === "select_vehicle") {
          setHighlight("vehicle");
          const n = `sel_${myRun}_${i}`;
          setHistCmd({ type: "select", vehicle_id: s.params.vehicle_id, nonce: n });
          await waitAck(n, 8000);
          await sleep(650);
        } else if (s.action === "set_period") {
          setHighlight("period");
          const n = `per_${myRun}_${i}`;
          setHistCmd({ type: "period", date_from: s.params.date_from, date_to: s.params.date_to, nonce: n });
          await waitAck(n, 8000);
          await sleep(650);
        } else if (s.action === "load_history") {
          setHighlight("load");
          const n = `load_${myRun}_${i}`;
          setHistCmd({ type: "load", vehicle_id: vid, date_from: p.date_from, date_to: p.date_to, nonce: n });
          const res = await waitAck(n, 30000);
          const cnt = (res && res.count != null) ? res.count : 0;
          log(`Résultats chargés : ${cnt} trajet(s).`, "ok");
          setHighlight(null);
          await sleep(300);
        } else if (s.action === "prepare_report") {
          setReport({ pdf_url: `${BACKEND_URL}${s.params.pdf_url}`, csv_url: `${BACKEND_URL}${s.params.csv_url}`, scope_label: s.params.scope_label });
          await sleep(400);
        } else if (s.action === "finish") {
          // rien : commentaire déjà posé
        }
        setStep(i, "done");
      }
      setStatus("done");
      controlRef.current = "idle";
      setHistCmd(null);
      log("Scénario terminé.", "ok");
    } catch (e) {
      if (e.message === "__stopped__") return;
      setStatus("error");
      controlRef.current = "idle";
      setHistCmd(null); setHighlight(null);
      setStepStates((arr) => arr.map((x) => (x === "active" ? "error" : x)));
      log(e.message === "timeout" ? "L'étape n'a pas été confirmée dans l'UI (délai dépassé). Scénario interrompu." : "Erreur pendant l'exécution.", "error");
    }
  }, [log, navigate]);

  // Lance un plan reçu du serveur (déjà validé/RBAC côté serveur).
  const startPlan = useCallback((p) => { clear(); setTimeout(() => runPlan(p), 0); }, [clear, runPlan]);

  // ---- détection de navigation manuelle : met en pause le scénario ----
  useEffect(() => {
    if (controlRef.current === "running" && expectedPathRef.current && location.pathname !== expectedPathRef.current) {
      controlRef.current = "paused"; setStatus("paused");
      setHistCmd(null);
      log("Navigation manuelle détectée : scénario mis en pause.", "warn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ---- ISOLATION : purge à tout changement de compte / déconnexion ----
  const uidRef = useRef(user?.user_id || null);
  useEffect(() => {
    const uid = user?.user_id || null;
    if (uid !== uidRef.current) { uidRef.current = uid; clear(); }
  }, [user?.user_id, clear]);
  useEffect(() => {
    const onUnauth = () => clear();
    window.addEventListener("auth:unauthorized", onUnauth);
    return () => window.removeEventListener("auth:unauthorized", onUnauth);
  }, [clear]);

  const value = {
    plan, status, stepStates, logs, report, highlight, histCmd,
    startPlan, pause, resume, stop, clear, ready, ack,
    // helper appelé par la page de chargement
    runHistoryLoad: async ({ vehicle_id, date_from, date_to }) => {
      const params = new URLSearchParams({ date_from, date_to });
      if (vehicle_id) params.set("vehicle_ids", vehicle_id);
      const r = await api.get(`/fleet-trips?${params.toString()}`);
      return r.data;
    },
  };
  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}
